import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';

const ChatBubble = () => {
  const { user, isAuthenticated } = useAuth();
  const chatCtx = useChat();

  const { unreadCount = 0, incomingToast = null, clearToast = () => {}, isOnline = () => false } = chatCtx || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState(null);
  const [toastVisible, setToastVisible] = useState(false);

  const isChatUser = isAuthenticated && user && ['admin', 'principal', 'class_teacher'].includes(user.role);
  const isOnChatPage = location.pathname === '/chat';

  // Show / auto-dismiss toast on incoming message
  useEffect(() => {
    if (!incomingToast || isOnChatPage) {
      if (incomingToast) clearToast();
      return;
    }
    clearToast();
    setToast(incomingToast);
    setToastVisible(true);

    const hide = setTimeout(() => setToastVisible(false), 4500);
    const clear = setTimeout(() => setToast(null), 5000);
    return () => { clearTimeout(hide); clearTimeout(clear); };
  }, [incomingToast, isOnChatPage, clearToast]);

  if (!isChatUser || isOnChatPage) return null;

  const handleGoToChat = () => {
    setToast(null);
    navigate('/chat');
  };

  const initials = (name) =>
    name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  return (
    <div className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-[9999] flex flex-col items-end gap-3 pointer-events-none">
      {/* Toast card */}
      {toast && (
        <div
          className={`pointer-events-auto flex items-center gap-3 bg-white rounded-2xl shadow-2xl border border-gray-100 p-3 max-w-[280px] cursor-pointer
            transition-all duration-300 ${toastVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
          onClick={handleGoToChat}
        >
          {/* Avatar */}
          <div className="shrink-0 relative">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-700">
              {initials(toast.senderName)}
            </div>
            <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${isOnline(toast.senderId) ? 'bg-green-500' : 'bg-gray-300'}`} />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-900 truncate">{toast.senderName}</p>
            <p className="text-xs text-gray-500 truncate mt-0.5">{toast.content}</p>
          </div>

          <button
            onClick={e => { e.stopPropagation(); setToastVisible(false); setTimeout(() => setToast(null), 300); }}
            className="shrink-0 text-gray-300 hover:text-gray-500 text-base leading-none"
          >
            ✕
          </button>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={handleGoToChat}
        className="pointer-events-auto relative w-14 h-14 bg-indigo-600 rounded-full shadow-xl flex items-center justify-center text-white hover:bg-indigo-700 active:scale-95 transition-all duration-150"
        title="Open Chat"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>

        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 shadow ring-2 ring-white animate-bounce">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    </div>
  );
};

export default ChatBubble;
